import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type { Invitation, Role } from '@up2d8/contracts';
import { Button, Callout, Dialog, Input, Select, useToast } from '@up2d8/ui';
import { localized } from '../../shared/format';
import { invitationToast } from '../../shared/invitation-toast';
import { useSession } from '../../shared/session';

/*
  Het scherm Uitnodigingen staat sinds BHR-3 in `invitations-screen.tsx`. Hier blijft het venster
  "Beheerder uitnodigen": het medewerkersscherm gebruikt het ook (M4 in deel 02), en wie dat
  venster herontwerpt, hoeft het scherm niet aan te raken.
*/

/** Uitnodigen: e-mail, naam en rol. */
export function InviteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    setEmail('');
    setDisplayName('');
    setRoleId('');
    setFout(null);
  }, [open]);

  /*
    De keuzelijst toont alleen rollen die déze beheerder mag toekennen.

    De server weigert de rest ook (een rol met meer rechten dan je zelf hebt is `403`), maar een
    lijst met opties die altijd falen, is een lijst die mensen leert dat het systeem willekeurig
    is. Zonder `access.role.assign` staat er helemaal geen keuzelijst: dan wordt het Beheerder.
  */
  const roles = useQuery({
    queryKey: ['access', 'roles'],
    enabled: open && has('access.role.assign'),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/roles');
      return (data as { roles: Role[] } | undefined)?.roles ?? [];
    },
  });

  const invite = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/api/v1/invitations', {
        body: {
          email: email.trim(),
          displayName: displayName.trim(),
          roleHint: 'admin' as const,
          roleId: roleId || null,
        } as never,
      });
      if (error) throw error;
      return data as Invitation | undefined;
    },
    onSuccess: async (invitation) => {
      onOpenChange(false);
      await queryClient.invalidateQueries({ queryKey: ['identity', 'invitations'] });
      // Wat er echt gebeurde: de uitnodiging kan bestaan zonder dat de mail aankwam.
      const uitkomst = invitationToast(invitation);
      toast.show({ title: t(uitkomst.key), tone: uitkomst.failed ? 'danger' : 'success' });
    },
    // Elke code een eigen zin: "er ging iets mis" laat de beheerder raden of hij het adres
    // verkeerd typte, of dat die persoon er allang is.
    onError: (error) => {
      switch (problemCode(error)) {
        case 'ALREADY_MEMBER':
          setFout(t('invitations.alreadyMember'));
          break;
        case 'ALREADY_INVITED':
          setFout(t('invitations.alreadyInvited'));
          break;
        case 'FORBIDDEN':
        case 'POLICY_DENIED':
          setFout(t('invitations.roleTooBig'));
          break;
        case 'RATE_LIMITED':
          setFout(t('invitations.rateLimited'));
          break;
        default:
          setFout(t('errors.generic'));
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('invitations.invite')}
      description={t('invitations.inviteHint')}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            form="beheerder-uitnodigen"
            type="submit"
            data-testid="send-invite"
            loading={invite.isPending}
            disabled={email.trim() === '' || displayName.trim().length < 2}
          >
            {t('invitations.send')}
          </Button>
        </>
      }
    >
      <form
        id="beheerder-uitnodigen"
        className="flex flex-col gap-element"
        onSubmit={(event) => {
          event.preventDefault();
          setFout(null);
          if (email.trim() && displayName.trim().length >= 2) invite.mutate();
        }}
      >
        <Input
          label={t('people.name')}
          data-testid="invite-name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
        <Input
          label={t('login.email')}
          type="email"
          data-testid="invite-email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        {has('access.role.assign') ? (
          <Select
            label={t('roles.title')}
            hint={t('invitations.roleHint')}
            data-testid="invite-role"
            value={roleId}
            onChange={(event) => setRoleId(event.target.value)}
          >
            <option value="">{t('invitations.roleAdmin')}</option>
            {(roles.data ?? [])
              .filter((role) => role.key !== 'admin')
              .map((role) => (
                <option key={role.id} value={role.id}>
                  {localized(role.name as Record<string, string>) ?? role.id}
                </option>
              ))}
          </Select>
        ) : null}

        {fout ? (
          <Callout tone="danger" data-testid="invite-error">
            {fout}
          </Callout>
        ) : null}

        <p className="flex items-start gap-2 text-caption text-text-muted">
          <Mail aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          {t('invitations.mailHint')}
        </p>
      </form>
    </Dialog>
  );
}
